import { useEffect, useState } from "react";
import { api, formatApiError, ksh, API } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, BarChart3, Smartphone, Clock, Truck, MapPin, Copy,
  Home as HomeIcon, CheckCircle2, Send, Printer, ShieldCheck,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { hasPerm, maskPhone } from "@/components/admin/_shared";
import { devLog } from "@/lib/devLog";

// ---- Dispatch tab: ready orders grouped by delivery channel ----
const DISPATCH_CHANNELS = [
  {
    key: "pickup",
    label: "Walk-in pickup",
    sub: "Customer collects in shop",
    icon: HomeIcon,
    accent: "#059669",
  },
  {
    key: "nairobi_delivery",
    label: "Nairobi delivery",
    sub: "Same-day courier within Nairobi",
    icon: MapPin,
    accent: "#0EA5E9",
  },
  {
    key: "countrywide",
    label: "Countrywide",
    sub: "Out-of-town courier / parcel service",
    icon: Truck,
    accent: "#DC2626",
  },
];

function DispatchTally({ tally, onRefresh }) {
  if (!tally) {
    return (
      <div className="bg-white border border-[#E5E5E5] p-4 flex items-center gap-3" data-testid="dispatch-tally-loading">
        <Loader2 className="w-4 h-4 animate-spin text-[#525252]" />
        <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252]">Loading today&apos;s courier tally…</span>
      </div>
    );
  }

  const tiles = [
    {
      key: "mpesa",
      label: "M-Pesa fees collected",
      total: tally.mpesa_total,
      count: tally.mpesa_count,
      sub: tally.mpesa_count === 1 ? "1 order paid in" : `${tally.mpesa_count} orders paid in`,
      accent: "#059669",
      icon: Smartphone,
    },
    {
      key: "cash",
      label: "Cash to couriers",
      total: tally.cash_total,
      count: tally.cash_count,
      sub: tally.cash_count === 1 ? "1 order · cash on delivery" : `${tally.cash_count} orders · cash on delivery`,
      accent: "#0A0A0A",
      icon: Truck,
    },
    {
      key: "pending",
      label: "STKs awaiting customer",
      total: tally.pending_total,
      count: tally.pending_count,
      sub: tally.pending_count === 1 ? "1 STK awaiting PIN" : `${tally.pending_count} STKs awaiting PIN`,
      accent: "#F59E0B",
      icon: Clock,
    },
  ];

  return (
    <div className="bg-white border border-[#0A0A0A]" data-testid="dispatch-tally">
      <div className="px-5 py-3 border-b border-[#E5E5E5] flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-baseline gap-2">
          <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#525252]">Today · {tally.day_label}</span>
          <span className="text-[10px] text-[#A3A3A3]">Nairobi time</span>
        </div>
        <button
          onClick={onRefresh}
          data-testid="dispatch-tally-refresh"
          className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] hover:text-[#0A0A0A] inline-flex items-center gap-1"
        >
          <BarChart3 className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 divide-x divide-[#E5E5E5]">
        {tiles.map((t) => {
          const Icon = t.icon;
          const isPending = t.key === "pending";
          return (
            <div
              key={t.key}
              className="p-5"
              data-testid={`dispatch-tally-${t.key}`}
              style={{ borderTop: `3px solid ${t.accent}` }}
            >
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold mb-2" style={{ color: t.accent }}>
                <Icon className="w-3.5 h-3.5" /> {t.label}
              </div>
              {isPending ? (
                <div className="font-display font-black text-3xl mono">{t.count}</div>
              ) : (
                <div className="font-display font-black text-3xl mono">{ksh(t.total || 0)}</div>
              )}
              <div className="text-xs text-[#525252] mt-1">{t.sub}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function DispatchTab({ user }) {
  const isSuper = user?.role === "super_admin" || user?.role === "admin";
  const canPII = hasPerm(user, "orders.view_pii");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [channel, setChannel] = useState("all");
  const [search, setSearch] = useState("");
  const [tally, setTally] = useState(null);

  const loadTally = async () => {
    try {
      const { data } = await api.get("/admin/dispatch/tally");
      setTally(data);
    } catch (e) {
      // Non-blocking: tally is decorative
      devLog.warn("dispatch tally failed", e);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/orders");
      setOrders(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not load orders");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); loadTally(); }, []);

  const completeOrder = async (id, tracking) => {
    try {
      await api.patch(`/admin/orders/${id}/status`, { status: "completed" });
      toast.success(`${tracking} dispatched & completed`);
      load();
      loadTally();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not complete order");
    }
  };

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-[#059669]" />;

  const readyOrders = orders.filter((o) => o.status === "ready");
  const matchesSearch = (o) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      (o.tracking_number || "").toLowerCase().includes(q) ||
      (o.customer_name || "").toLowerCase().includes(q) ||
      (o.customer_phone || "").toLowerCase().includes(q) ||
      (o.delivery_recipient_name || "").toLowerCase().includes(q) ||
      (o.delivery_address || "").toLowerCase().includes(q) ||
      (o.delivery_town || "").toLowerCase().includes(q)
    );
  };

  const grouped = DISPATCH_CHANNELS.reduce((acc, c) => {
    acc[c.key] = readyOrders.filter((o) => (o.delivery_method || "pickup") === c.key).filter(matchesSearch);
    return acc;
  }, {});

  const counts = DISPATCH_CHANNELS.map((c) => ({ ...c, count: grouped[c.key].length }));
  const totalShown = counts.reduce((s, c) => s + c.count, 0);

  return (
    <div className="space-y-6" data-testid="dispatch-tab">
      <DispatchTally tally={tally} onRefresh={loadTally} />
      {/* Header + search + channel filter */}
      <div className="bg-white border border-[#E5E5E5] p-5">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252] mb-1">§ Hand-off desk</div>
            <h2 className="font-display font-black text-2xl">Dispatch board</h2>
            <p className="text-xs text-[#525252] mt-1">
              Every order marked <span className="font-bold text-[#059669]">Ready</span>, grouped by how it leaves the shop.
              {!isSuper && " You can mark these as completed when handed over."}
            </p>
          </div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold">
            <span className="text-[#525252]">Ready right now</span>
            <span className="px-2 py-1 border border-[#059669] bg-[#059669] text-white mono">{readyOrders.length}</span>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tracking #, customer, recipient, address, town..."
            data-testid="dispatch-search"
            className="flex-1 min-w-[200px] px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none text-sm"
          />
          <button
            onClick={() => setChannel("all")}
            data-testid="dispatch-channel-all"
            className={`px-3 py-2 text-[10px] uppercase tracking-[0.15em] font-bold border whitespace-nowrap ${channel === "all" ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5E5] text-[#525252] hover:border-[#0A0A0A]"}`}
          >
            All channels · {readyOrders.length}
          </button>
          {counts.map((c) => (
            <button
              key={c.key}
              onClick={() => setChannel(c.key)}
              data-testid={`dispatch-channel-${c.key}`}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-[10px] uppercase tracking-[0.15em] font-bold border whitespace-nowrap ${channel === c.key ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5E5] text-[#525252] hover:border-[#0A0A0A]"}`}
            >
              <c.icon className="w-3.5 h-3.5" /> {c.label} · {c.count}
            </button>
          ))}
        </div>
      </div>

      {totalShown === 0 && (
        <div className="border-2 border-dashed border-[#E5E5E5] bg-white p-10 text-center" data-testid="dispatch-empty">
          <CheckCircle2 className="w-10 h-10 text-[#059669] mx-auto mb-3" />
          <div className="font-display font-bold text-xl">All clear · nothing to dispatch</div>
          <div className="text-sm text-[#525252] mt-1">Orders show up here the moment fulfilment staff mark them <span className="text-[#059669] font-bold">Ready</span>.</div>
        </div>
      )}

      {DISPATCH_CHANNELS.filter((c) => channel === "all" || channel === c.key).map((c) => {
        const items = grouped[c.key];
        if (items.length === 0 && channel !== c.key) return null;
        return (
          <section key={c.key} data-testid={`dispatch-section-${c.key}`}>
            <div className="flex items-center gap-3 mb-3">
              <div
                className="w-10 h-10 border-2 flex items-center justify-center"
                style={{ borderColor: c.accent, color: c.accent }}
              >
                <c.icon className="w-5 h-5" strokeWidth={2.5} />
              </div>
              <div>
                <div className="font-display font-black text-xl" style={{ color: c.accent }}>{c.label}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#525252]">{c.sub} · {items.length} order{items.length === 1 ? "" : "s"}</div>
              </div>
            </div>

            {items.length === 0 ? (
              <div className="border border-dashed border-[#E5E5E5] bg-white p-5 text-xs uppercase tracking-[0.2em] text-[#A3A3A3] text-center">
                No orders in this channel right now.
              </div>
            ) : (
              <div className="grid gap-3 md:grid-cols-2">
                {items.map((o) => (
                  <DispatchCard
                    key={o.id}
                    order={o}
                    channel={c}
                    canPII={canPII}
                    onComplete={() => completeOrder(o.id, o.tracking_number)}
                  />
                ))}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

function DispatchCard({ order, channel, canPII, onComplete }) {
  const isPickup = (order.delivery_method || "pickup") === "pickup";
  const Icon = channel.icon;
  const displayPhone = (p) => (canPII ? p : maskPhone(p));
  const [downloadingLabel, setDownloadingLabel] = useState(false);

  const downloadShippingLabel = async () => {
    setDownloadingLabel(true);
    try {
      const token = localStorage.getItem("ctp_token");
      const res = await fetch(`${API}/admin/orders/${order.id}/shipping-label.pdf`, {
        method: "GET",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `label-${order.tracking_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      toast.success("Shipping label downloaded");
    } catch (e) {
      toast.error(formatApiError(e?.message) || "Could not download shipping label");
    } finally {
      setDownloadingLabel(false);
    }
  };

  // Compose courier handoff text
  const handoffText = (() => {
    const lines = [];
    lines.push(`CTP Kenya · Order ${order.tracking_number}`);
    lines.push(`Channel: ${channel.label}`);
    if (isPickup) {
      lines.push(`Pickup by: ${order.customer_name} · ${displayPhone(order.customer_phone)}`);
    } else {
      const recName = order.delivery_recipient_name || order.customer_name;
      const recPhone = order.delivery_recipient_phone || order.customer_phone;
      lines.push(`Recipient: ${recName} · ${displayPhone(recPhone)}`);
      if (order.delivery_address) lines.push(`Address: ${order.delivery_address}`);
      const locale = [order.delivery_town, order.delivery_county].filter(Boolean).join(", ");
      if (locale) lines.push(`Locale: ${locale}`);
      if (order.delivery_instructions) lines.push(`Instructions: ${order.delivery_instructions}`);
    }
    lines.push(`Items: ${order.items?.length || 0} · Total: KES ${order.total}`);
    lines.push(`Payment: ${order.payment_status}`);
    if (!canPII) lines.push(`Note: phone numbers redacted — request from a senior to share with courier.`);
    return lines.join("\n");
  })();

  const copyHandoff = async () => {
    try {
      await navigator.clipboard.writeText(handoffText);
      toast.success("Courier hand-off copied");
    } catch {
      toast.error("Clipboard blocked — long-press to copy manually");
    }
  };

  return (
    <div
      className="bg-white border border-[#E5E5E5] hover:border-[#0A0A0A] transition-colors"
      data-testid={`dispatch-card-${order.tracking_number}`}
      style={{ borderTop: `3px solid ${channel.accent}` }}
    >
      <div className="p-4 border-b border-[#E5E5E5] flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="mono font-bold text-sm">{order.tracking_number}</span>
            {order.tier && order.tier !== "normal" && (
              <span
                className={`px-1.5 py-0.5 border text-[9px] uppercase tracking-[0.15em] font-bold ${
                  order.tier === "priority"
                    ? "border-[#DC2626] bg-[#DC2626]/10 text-[#DC2626]"
                    : "border-[#F59E0B] bg-[#F59E0B]/10 text-[#92400E]"
                }`}
              >
                {order.tier === "priority" ? "⚡ Priority" : "⚡ Urgent"}
              </span>
            )}
            {order.payment_status !== "paid" && (
              <span className="px-1.5 py-0.5 border text-[9px] uppercase tracking-[0.15em] font-bold border-[#F59E0B] bg-[#F59E0B]/10 text-[#92400E]">
                Unpaid
              </span>
            )}
          </div>
          <div className="font-display font-bold text-lg leading-tight truncate">{order.customer_name}</div>
          <div className="text-xs text-[#525252] mono">{displayPhone(order.customer_phone)}</div>
        </div>
        <div className="text-right">
          <div className="text-[9px] uppercase tracking-[0.2em] text-[#525252]">Total</div>
          <div className="font-display font-black text-xl mono">{ksh(order.total)}</div>
        </div>
      </div>

      {/* Delivery / pickup block */}
      <div className="p-4 space-y-2 bg-[#FAFAFA]">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: channel.accent }}>
          <Icon className="w-3.5 h-3.5" /> {isPickup ? "Shop pickup" : "Courier hand-off"}
        </div>
        {isPickup ? (
          <div className="text-sm">
            Customer <span className="font-bold">{order.customer_name}</span> will collect in person.
          </div>
        ) : (
          <dl className="text-sm space-y-1.5" data-testid={`dispatch-recipient-${order.tracking_number}`}>
            <DispatchRow label="Recipient">
              <span className="font-bold">{order.delivery_recipient_name || order.customer_name}</span>
            </DispatchRow>
            <DispatchRow label="Phone">
              <a href={canPII ? `tel:${order.delivery_recipient_phone || order.customer_phone}` : undefined} className={`mono font-bold ${canPII ? "hover:text-[#059669]" : ""}`}>
                {displayPhone(order.delivery_recipient_phone || order.customer_phone)}
              </a>
            </DispatchRow>
            {order.delivery_address && (
              <DispatchRow label="Address"><span>{order.delivery_address}</span></DispatchRow>
            )}
            {(order.delivery_town || order.delivery_county) && (
              <DispatchRow label="Locale">
                <span>{[order.delivery_town, order.delivery_county].filter(Boolean).join(", ")}</span>
              </DispatchRow>
            )}
            {order.delivery_instructions && (
              <DispatchRow label="Notes"><span className="italic text-[#525252]">{order.delivery_instructions}</span></DispatchRow>
            )}
          </dl>
        )}
      </div>

      {!isPickup && (
        <DeliveryFeePanel order={order} />
      )}

      <div className="p-4 border-t border-[#E5E5E5] flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[11px] text-[#525252]">
          {order.items?.length || 0} item{(order.items?.length || 0) === 1 ? "" : "s"} ·{" "}
          <span className={order.payment_status === "paid" ? "text-[#059669]" : "text-[#F59E0B]"}>
            {order.payment_status}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {order.payment_status !== "paid" && <OrderPaymentRetry order={order} />}
          <button
            onClick={copyHandoff}
            data-testid={`dispatch-copy-${order.tracking_number}`}
            className="inline-flex items-center gap-1 px-3 py-2 border border-[#0A0A0A] text-[#0A0A0A] text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-[#0A0A0A] hover:text-white"
          >
            <Copy className="w-3 h-3" /> {isPickup ? "Copy details" : "Copy for courier"}
          </button>
          {!isPickup && (
            <button
              onClick={downloadShippingLabel}
              disabled={downloadingLabel}
              data-testid={`dispatch-label-${order.tracking_number}`}
              title="Download printable shipping label (From → To)"
              className="inline-flex items-center gap-1 px-3 py-2 border border-[#0A0A0A] text-[#0A0A0A] text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-[#0A0A0A] hover:text-white disabled:opacity-50"
            >
              {downloadingLabel ? <Loader2 className="w-3 h-3 animate-spin" /> : <Printer className="w-3 h-3" />}
              Shipping label
            </button>
          )}
          <button
            onClick={onComplete}
            disabled={order.payment_status !== "paid"}
            data-testid={`dispatch-complete-${order.tracking_number}`}
            title={order.payment_status !== "paid" ? "Unpaid orders cannot be completed" : "Mark as dispatched & completed"}
            className="inline-flex items-center gap-1 px-3 py-2 bg-[#059669] text-white text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-[#047857] disabled:opacity-50 disabled:hover:bg-[#059669]"
          >
            <CheckCircle2 className="w-3.5 h-3.5" /> Mark completed
          </button>
        </div>
      </div>
    </div>
  );
}

function DispatchRow({ label, children }) {
  return (
    <div className="flex items-baseline gap-2">
      <dt className="text-[10px] uppercase tracking-[0.2em] text-[#525252] w-20 flex-shrink-0">{label}</dt>
      <dd className="flex-1 min-w-0 break-words">{children}</dd>
    </div>
  );
}

function DeliveryFeePanel({ order }) {
  const existing = order.delivery_fee_status;
  const [amount, setAmount] = useState(order.delivery_fee || "");
  const [courier, setCourier] = useState(order.delivery_courier || "");
  const [phone, setPhone] = useState(order.delivery_recipient_phone || order.customer_phone || "");
  const [stkEnabled, setStkEnabled] = useState(true);
  const [busy, setBusy] = useState(false);
  const [localState, setLocalState] = useState(null);

  const state = localState || {
    status: existing,
    amount: order.delivery_fee,
    courier: order.delivery_courier,
    paid_at: order.delivery_fee_paid_at,
    receipt: order.delivery_fee_mpesa_receipt,
  };

  const submit = async (method) => {
    if (!courier) { toast.error("Enter the courier name first"); return; }
    if (method === "stk" && (!amount || parseFloat(amount) <= 0)) {
      toast.error("Enter a delivery fee amount for the STK push"); return;
    }
    if (method === "stk" && !phone) { toast.error("Phone number is required for STK push"); return; }
    setBusy(true);
    try {
      const payload = {
        amount: amount ? parseFloat(amount) : 0,
        courier,
        method,
      };
      if (method === "stk") payload.phone = phone;
      const { data } = await api.post(`/payments/admin/orders/${order.id}/delivery-fee`, payload);
      toast.success(data.message || "Recorded");
      setLocalState({
        status: data.delivery_fee_status,
        amount: data.delivery_fee,
        courier,
        paid_at: data.delivery_fee_status === "paid" ? new Date().toISOString() : null,
      });
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not record fee");
    } finally {
      setBusy(false);
    }
  };

  const statusBadge = {
    paid: { bg: "bg-[#059669]/10", text: "text-[#059669]", border: "border-[#059669]", label: "Paid via M-Pesa" },
    stk_sent: { bg: "bg-[#F59E0B]/10", text: "text-[#92400E]", border: "border-[#F59E0B]", label: "STK sent · awaiting customer" },
    on_delivery: { bg: "bg-[#0A0A0A]/5", text: "text-[#0A0A0A]", border: "border-[#0A0A0A]", label: "Cash to courier on delivery" },
    failed: { bg: "bg-[#DC2626]/10", text: "text-[#DC2626]", border: "border-[#DC2626]", label: "STK failed · retry" },
  }[state.status] || null;

  return (
    <div className="p-4 border-t border-[#E5E5E5] bg-white" data-testid={`delivery-fee-${order.tracking_number}`}>
      <div className="flex items-center justify-between mb-3 gap-2">
        <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252]">Courier fee</div>
        {statusBadge && (
          <span className={`px-2 py-0.5 border text-[9px] uppercase tracking-[0.2em] font-bold ${statusBadge.bg} ${statusBadge.text} ${statusBadge.border}`}>
            {statusBadge.label}
          </span>
        )}
      </div>
      {state.status === "paid" || state.status === "on_delivery" ? (
        <div className="text-sm space-y-1">
          <div>
            {state.amount && Number(state.amount) > 0 ? (
              <>
                <span className="font-display font-black text-xl mono">{ksh(state.amount)}</span>
                <span className="text-[#525252] ml-2">· {state.courier}</span>
              </>
            ) : (
              <span className="font-display font-bold text-base">Cash to {state.courier} on delivery</span>
            )}
          </div>
          {state.receipt && <div className="text-xs mono text-[#059669]">M-Pesa receipt: {state.receipt}</div>}
          {state.status === "on_delivery" && (
            <div className="text-xs text-[#525252] italic">Customer pays the courier directly — not collected by us.</div>
          )}
          <button
            onClick={() => { setLocalState(null); setAmount(""); setCourier(""); }}
            className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] hover:text-[#0A0A0A] mt-2"
            data-testid={`delivery-fee-edit-${order.tracking_number}`}
          >
            Edit
          </button>
        </div>
      ) : state.status === "stk_sent" || state.status === "failed" ? (
        <DeliveryFeeStkInFlight order={order} state={state} setLocalState={setLocalState} />
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[9px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">
                Fee (KES){stkEnabled ? "" : " · optional"}
              </label>
              <input type="number" min={0} max={100000} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={stkEnabled ? "e.g. 300" : "Leave blank if unknown"} data-testid={`delivery-fee-amount-${order.tracking_number}`} className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none text-sm mono" />
            </div>
            <div>
              <label className="block text-[9px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">Courier</label>
              <input type="text" value={courier} onChange={(e) => setCourier(e.target.value)} placeholder="e.g. Pickup Mtaani, Sendy, Bolt..." data-testid={`delivery-fee-courier-${order.tracking_number}`} className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none text-sm" />
            </div>
          </div>
          <div>
            <label className="block text-[9px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">Phone (for STK)</label>
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="07XXXXXXXX" disabled={!stkEnabled} data-testid={`delivery-fee-phone-${order.tracking_number}`} className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none text-sm mono disabled:bg-[#F5F5F5] disabled:text-[#A3A3A3] disabled:cursor-not-allowed" />
          </div>
          <div
            className="flex items-center justify-between gap-3 px-3 py-2.5 border border-[#E5E5E5] bg-[#FAFAFA]"
            data-testid={`delivery-fee-stk-toggle-row-${order.tracking_number}`}
          >
            <div className="min-w-0">
              <div className="text-xs font-bold text-[#0A0A0A]">
                {stkEnabled ? "STK delivery-fee push" : "Pay on delivery"}
              </div>
              <div className="text-[10px] text-[#525252] mt-0.5">
                {stkEnabled
                  ? "Customer pays the courier fee upfront via M-Pesa."
                  : "Customer pays the courier in cash on arrival (not in our books)."}
              </div>
            </div>
            <Switch
              checked={stkEnabled}
              onCheckedChange={setStkEnabled}
              disabled={busy}
              data-testid={`delivery-fee-stk-switch-${order.tracking_number}`}
              aria-label="Toggle STK delivery-fee push"
            />
          </div>
          <div className="flex flex-wrap gap-2 pt-1">
            <button
              onClick={() => submit(stkEnabled ? "stk" : "on_delivery")}
              disabled={busy || !courier || (stkEnabled && (!amount || !phone))}
              data-testid={`delivery-fee-submit-${order.tracking_number}`}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#0A0A0A] text-white text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-[#059669] disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              {stkEnabled ? "Send STK push" : "Mark pay on delivery"}
            </button>
          </div>
          <p className="text-[10px] text-[#525252]">
            Toggle the switch above to choose between an M-Pesa STK push (advance payment) and cash to the courier on delivery.
          </p>
        </div>
      )}
    </div>
  );
}

function OrderPaymentRetry({ order }) {
  const [busyA, setBusyA] = useState(false);
  const [busyB, setBusyB] = useState(false);

  const resend = async () => {
    setBusyA(true);
    try {
      const { data } = await api.post(`/payments/admin/orders/${order.id}/resend-stk`, { kind: "order" });
      toast.success(data.message || "Resent");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Resend failed");
    } finally {
      setBusyA(false);
    }
  };

  const confirm = async () => {
    setBusyB(true);
    try {
      const { data } = await api.post(`/payments/admin/orders/${order.id}/confirm-payment`, { kind: "order" });
      if (data.still_processing) { toast.info(data.message); return; }
      if (data.confirmed) toast.success(data.message);
      else toast.error(data.message || "Not paid yet");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Confirm failed");
    } finally {
      setBusyB(false);
    }
  };

  return (
    <>
      <button
        onClick={resend}
        disabled={busyA}
        data-testid={`order-payment-resend-${order.tracking_number}`}
        title="Re-send the customer their M-Pesa prompt for this order"
        className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#F59E0B] text-[#92400E] text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-[#F59E0B] hover:text-white disabled:opacity-50"
      >
        {busyA ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
        Resend STK
      </button>
      <button
        onClick={confirm}
        disabled={busyB}
        data-testid={`order-payment-confirm-${order.tracking_number}`}
        title="Ask Safaricom if this STK was actually paid"
        className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#059669] text-[#059669] text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-[#059669] hover:text-white disabled:opacity-50"
      >
        {busyB ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />}
        Confirm payment
      </button>
    </>
  );
}

function DeliveryFeeStkInFlight({ order, state, setLocalState }) {
  const [busyA, setBusyA] = useState(false);
  const [busyB, setBusyB] = useState(false);

  const resend = async () => {
    setBusyA(true);
    try {
      const { data } = await api.post(`/payments/admin/orders/${order.id}/resend-stk`, { kind: "delivery_fee" });
      toast.success(data.message || "Resent");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Resend failed");
    } finally {
      setBusyA(false);
    }
  };

  const confirm = async () => {
    setBusyB(true);
    try {
      const { data } = await api.post(`/payments/admin/orders/${order.id}/confirm-payment`, { kind: "delivery_fee" });
      if (data.still_processing) { toast.info(data.message); return; }
      if (data.confirmed) {
        toast.success(data.message);
        setLocalState({ status: "paid", amount: state.amount, courier: state.courier, receipt: data.message?.match(/receipt ([A-Z0-9]+)/)?.[1] });
      } else {
        toast.error(data.message || "Not paid yet");
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Confirm failed");
    } finally {
      setBusyB(false);
    }
  };

  return (
    <div className="space-y-2 text-sm" data-testid={`delivery-fee-inflight-${order.tracking_number}`}>
      <div>
        <span className="font-display font-black text-xl mono">{ksh(state.amount)}</span>
        <span className="text-[#525252] ml-2">· {state.courier}</span>
      </div>
      <div className="text-xs text-[#525252]">
        Customer hasn&rsquo;t confirmed yet. Resend the prompt or ask Safaricom directly if it&rsquo;s been paid.
      </div>
      <div className="flex flex-wrap gap-2 pt-1">
        <button
          onClick={resend}
          disabled={busyA}
          data-testid={`delivery-fee-resend-${order.tracking_number}`}
          className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#0A0A0A] text-[#0A0A0A] text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-[#0A0A0A] hover:text-white disabled:opacity-50"
        >
          {busyA ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          Resend STK
        </button>
        <button
          onClick={confirm}
          disabled={busyB}
          data-testid={`delivery-fee-confirm-${order.tracking_number}`}
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#059669] text-white text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-[#047857] disabled:opacity-50"
        >
          {busyB ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
          Confirm payment
        </button>
        <button
          onClick={() => setLocalState(null)}
          className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] hover:text-[#0A0A0A] ml-1 self-center"
        >
          Edit details
        </button>
      </div>
    </div>
  );
}
